// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

// Load polyfills first before any other imports
;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:slug\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/articles/:category\",\"regex\":\"^/articles/([^/]+)$\",\"paramNames\":[\"category\"],\"hasOptionalPrefix\":false},{\"path\":\"/articles/:category/:slug\",\"regex\":\"^/articles/([^/]+)/([^/]+)$\",\"paramNames\":[\"category\",\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/authors/:slug\",\"regex\":\"^/authors/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/faq/:category\",\"regex\":\"^/faq/([^/]+)$\",\"paramNames\":[\"category\"],\"hasOptionalPrefix\":false},{\"path\":\"/item/:id*?\",\"regex\":\"^/item(?:/(.*))?$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/profile/messages/:id\",\"regex\":\"^/profile/messages/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/profile/my-vehicles/:id\",\"regex\":\"^/profile/my-vehicles/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/retailers/:slug\",\"regex\":\"^/retailers/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/search/:slug*?\",\"regex\":\"^/search(?:/(.*))?$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/sell-to-dealer/:slug*\",\"regex\":\"^/sell-to-dealer/(.+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/sell-to-dealer/created/:id\",\"regex\":\"^/sell-to-dealer/created/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/valuate-vehicle/:slug*\",\"regex\":\"^/valuate-vehicle/(.+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/articles\"},{\"path\":\"/articles/search\"},{\"path\":\"/authors\"},{\"path\":\"/faq\"},{\"path\":\"/faq/search\"},{\"path\":\"/oidc/remove-account\"},{\"path\":\"/oidc/signin\"},{\"path\":\"/oidc/signout\"},{\"path\":\"/profile\"},{\"path\":\"/profile/account\"},{\"path\":\"/profile/assignments\"},{\"path\":\"/profile/messages\"},{\"path\":\"/profile/my-vehicles\"},{\"path\":\"/profile/search-subscriptions\"},{\"path\":\"/profile/vehicle-subscriptions\"},{\"path\":\"/retailers\"},{\"path\":\"/sell-to-dealer\"},{\"path\":\"/sentry-example-page\"},{\"path\":\"/valuate-vehicle\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.0.7";import './polyfills/bigint';
import './polyfills/structured-clone';

import * as Sentry from '@sentry/nextjs';

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
  release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,

  // Disable performance monitoring (only track errors and sessions)
  tracesSampleRate: 0,

  // Disable logs to reduce quota usage
  enableLogs: false,

  // Disable session tracking to reduce traffic and I/O pool usage
  integrations: (defaultIntegrations) => {
    return defaultIntegrations.filter((integration) => integration.name !== 'BrowserSession');
  },

  // Disable SDK telemetry reports
  sendClientReports: false,

  // Use tunnel to avoid ad blockers
  tunnel: '/api/s/e',

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  // Note: thirdPartyErrorFilterIntegration is not used because it requires webpack plugin
  // which doesn't work with Turbopack. We use manual filtering in beforeSend instead.

  // Filter out noise
  ignoreErrors: [
    // Browser extensions
    'top.GLOBALS',
    'chrome-extension://',
    'moz-extension://',
    // Browser extension runtime errors
    'runtime.sendMessage',
    /Invalid call to runtime\.sendMessage/,
    // Network errors
    'NetworkError',
    'Network request failed',
    'Failed to fetch',
    // User cancelled requests
    'AbortError',
    // React Native WebView errors (https://github.com/facebook/react/issues/34053)
    'Error invoking postMessage: Java object is gone',
    // Facebook Android WebView errors - Facebook's injected scripts fail when Java bridge is destroyed
    // Occurs when users navigate away or close the page in Facebook in-app browser
    // https://github.com/getsentry/sentry-javascript/issues/2744
    'Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone',
    /Java object is gone/i,
    // Broad filter for all Java bridge errors (space after "java" avoids matching "javascript")
    /java /i,
    // iOS WebKit WebView errors - scripts trying to access unavailable message handlers
    // Common in Instagram, Facebook, and other in-app browsers
    // https://github.com/getsentry/sentry-javascript/issues/3040
    /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/,
    /window\.webkit\.messageHandlers is undefined/,
    /Cannot read property 'messageHandlers' of undefined/,
    // Third-party scripts (GTM, etc.) trying to access undefined globals
    /Can't find variable: wayke/,
    /wayke is not defined/,
    // Third-party tracking pixels not loaded (TikTok, Facebook, etc.)
    'ttq is not defined',
    /Can't find variable: ttq/,
    'fbq is not defined',
    'gtag is not defined',
    // Google reCAPTCHA timeout errors - known issue, doesn't affect functionality
    // https://github.com/dozoisch/react-google-recaptcha/issues/103
    // https://github.com/google/recaptcha/issues/269
    'Timeout',
    /Timeout \([a-z]\)/i,
    /recaptcha.*timeout/i,
  ],

  beforeSend(event, _hint) {
    // Filter reCAPTCHA timeout errors from Google's script
    // These are internal false positives that don't affect functionality
    if (event.exception?.values?.[0]) {
      const errorMessage = event.exception.values[0].value || '';
      const stacktrace = event.exception.values[0].stacktrace;

      // Check if this is a reCAPTCHA timeout error
      if (
        errorMessage.includes('Timeout') &&
        stacktrace?.frames?.some((frame) => {
          const filename = frame.filename || '';
          return filename.includes('recaptcha');
        })
      ) {
        return null;
      }

      // Filter errors originating from GTM (Google Tag Manager)
      // GTM often triggers errors from third-party tracking scripts it loads
      if (
        stacktrace?.frames?.some((frame) => {
          const filename = frame.filename || '';
          return filename.includes('gtm.js') || filename.includes('gtag/js');
        })
      ) {
        return null;
      }
    }

    // Check for parentNode errors and filter if from third-party code
    if (event.exception?.values?.[0]) {
      const errorMessage = event.exception.values[0].value || '';
      const stacktrace = event.exception.values[0].stacktrace;

      // Check if this is a parentNode error
      const isParentNodeError =
        errorMessage.includes('parentNode') ||
        errorMessage.match(/null is not an object \(evaluating '.*\.parentNode'\)/) ||
        errorMessage.match(/Cannot read propert(y|ies) .* 'parentNode'/);

      if (isParentNodeError && stacktrace?.frames) {
        // Tag for tracking
        event.tags = event.tags || {};
        event.tags.error_type = 'parentNode_access';

        // Check if error is from our own code
        const hasOwnCodeInStack = stacktrace.frames.some((frame) => {
          const filename = frame.filename || '';
          return (
            // Next.js production bundles
            filename.includes('/_next/static/') ||
            filename.includes('/chunks/') ||
            // Turbopack development files
            filename.includes('turbopack:///[project]/src/') ||
            filename.includes('turbopack:///[project]/app/')
          );
        });

        // If no frames from our code, it's third-party - filter it out
        if (!hasOwnCodeInStack) {
          return null;
        }

        // Add context about where in the stack this occurred (for our own errors)
        const topFrame = stacktrace.frames[stacktrace.frames.length - 1];
        if (topFrame?.filename) {
          event.tags.error_location = topFrame.filename.split('/').pop() || 'unknown';
        }
      }
    }

    // Check User-Agent from multiple sources
    const userAgent =
      event.request?.headers?.['user-agent'] ||
      event.request?.headers?.['User-Agent'] ||
      navigator?.userAgent ||
      '';

    // Check browser name from context
    const browserName = event.contexts?.browser?.name || '';

    // Filter querySelector/addEventListener errors from Facebook/Instagram in-app browsers
    // These are injected scripts trying to manipulate DOM elements that may not exist
    if (event.exception?.values?.[0]) {
      const errorMessage = event.exception.values[0].value || '';
      const isFacebookBrowser =
        browserName === 'Facebook' ||
        userAgent.includes('FBAN/') ||
        userAgent.includes('FBAV/') ||
        userAgent.includes('Instagram');

      if (
        isFacebookBrowser &&
        (errorMessage.includes('querySelector') || errorMessage.includes('addEventListener'))
      ) {
        return null;
      }
    }

    // Filter bot traffic (including monitoring tools, social media crawlers, and automation tools)
    if (
      !userAgent ||
      userAgent.match(
        /bot|crawler|spider|headless|phantomjs|selenium|puppeteer|playwright|prerender|pingdom|gtmetrix|statuscake|facebookexternalhit|twitterbot|linkedinbot|slackbot|whatsapp|telegrambot/i,
      )
    ) {
      return null;
    }

    // Filter headless browsers
    if (typeof browserName === 'string' && browserName.match(/headless/i)) {
      return null;
    }

    // Filter third-party errors (manual approach since thirdPartyErrorFilterIntegration
    // doesn't work with Turbopack)
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (frames && frames.length > 0) {
      // Filter out Sentry's own frames and known third-party domains
      const relevantFrames = frames.filter((f) => {
        const filename = f.filename || '';

        // Skip Sentry's own frames
        if (
          filename.includes('@sentry/') ||
          filename.includes('node_modules/@sentry/') ||
          filename.includes('turbopack:///[project]/node_modules/@sentry/')
        ) {
          return false;
        }

        // Skip external third-party scripts (absolute URLs from other domains)
        if (filename.startsWith('http://') || filename.startsWith('https://')) {
          try {
            const url = new URL(filename);
            // Only allow our own domain
            if (!url.hostname.includes('wayke.se') && !url.hostname.includes('drive.se')) {
              return false;
            }
          } catch {
            // If URL parsing fails, skip this frame
            return false;
          }
        }

        // Skip common third-party script patterns
        if (
          filename.includes('googletagmanager.com') ||
          filename.includes('google-analytics.com') ||
          filename.includes('clarity.ms') ||
          filename.includes('facebook.net') ||
          filename.includes('freespee.com') ||
          filename.includes('/gtm.js') ||
          filename.includes('/gtag/') ||
          filename.includes('/analytics.js')
        ) {
          return false;
        }

        return true;
      });

      // If no relevant frames left, it's likely injected code
      if (relevantFrames.length === 0) {
        return null;
      }

      // Check if any frame is from our own code (strict matching)
      const hasOwnCode = relevantFrames.some((frame) => {
        const filename = frame.filename || '';
        return (
          // Next.js production bundles
          filename.includes('/_next/static/') ||
          filename.includes('/chunks/') ||
          // Turbopack development files
          filename.includes('turbopack:///[project]/src/') ||
          filename.includes('turbopack:///[project]/app/') ||
          // App Router routes (but NOT simple script names like /gtm.js)
          (filename.startsWith('/') &&
            !filename.match(/^\/[^/]+\.(js|css)$/) && // Exclude /script.js patterns
            (filename.includes('/app/') ||
              filename.includes('/src/') ||
              filename.split('/').length > 3))
        );
      });

      // If no frames from our code, it's third-party
      if (!hasOwnCode) {
        return null;
      }
    }

    return event;
  },
});

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
